import React, { useEffect, useState, useMemo, useRef } from "react";
import { Trophy, Search, X, CheckCircle2, Circle, Sparkles, MoveRight, Pencil, Eye } from "lucide-react";
import { api, formatGBP, formatApiErrorDetail } from "../../lib/api";
import GroundEditor from "./GroundEditor";
import SideElevationView from "./SideElevationView";

/**
 * Virtual pitch view — clickable/drag-droppable sponsorship slots arranged on an SVG
 * football pitch. Each slot shows its sponsor (if assigned) or a pulsing "available" marker.
 * Sponsors are draggable from the right sidebar onto any slot.
 *
 * Two modes:
 *  - "sell" (default): drag-drop sponsors onto slots
 *  - "edit": GroundEditor — define the actual ground (stands, fences, dugouts per side)
 */
export default function PitchTab({ tenantId, queryString = "" }) {
    const [layout, setLayout] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [activeSlot, setActiveSlot] = useState(null);
    const [dragSponsorId, setDragSponsorId] = useState(null);
    const [hoverSlot, setHoverSlot] = useState(null);
    const [filter, setFilter] = useState("");
    const [mode, setMode] = useState("sell"); // "sell" | "edit"
    const [elevationSide, setElevationSide] = useState(null); // "N" | "E" | "S" | "W" | null

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const [layoutRes, spRes] = await Promise.all([
                api.get(`/pitch/layout${queryString}`),
                api.get(`/sponsors${queryString}`),
            ]);
            setLayout(layoutRes.data);
            setSponsors(spRes.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

    const assignedSponsorIds = useMemo(() => {
        if (!layout) return new Set();
        return new Set(Object.values(layout.assignments).map((a) => a.sponsor_id));
    }, [layout]);

    const unassigned = useMemo(() => {
        return sponsors
            .filter((s) => !assignedSponsorIds.has(s.id) && s.status !== "Cancelled")
            .filter((s) => !filter || s.name.toLowerCase().includes(filter.toLowerCase()));
    }, [sponsors, assignedSponsorIds, filter]);

    const assignedList = useMemo(() => {
        if (!layout) return [];
        return Object.values(layout.assignments);
    }, [layout]);

    const doAssign = async (slotKey, sponsorId) => {
        try {
            await api.post(`/pitch/assign${queryString}`, { slot_key: slotKey, sponsor_id: sponsorId });
            await load();
            setActiveSlot(slotKey);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const doUnassign = async (slotKey) => {
        try {
            await api.delete(`/pitch/assign/${slotKey}${queryString}`);
            await load();
            setActiveSlot(null);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (loading && !layout) {
        return <div className="border hairline p-12 text-center text-zinc-500 text-sm" data-testid="pitch-loading">Loading pitch…</div>;
    }
    if (err && !layout) {
        return <div className="border border-rose-500/40 text-rose-300 text-sm p-6" data-testid="pitch-error">{err}</div>;
    }
    if (!layout) return null;

    // EDIT MODE — show GroundEditor
    if (mode === "edit") {
        return (
            <GroundEditor
                tenantId={tenantId}
                queryString={queryString}
                initialGround={layout.ground}
                onClose={() => setMode("sell")}
                onSaved={async () => { await load(); setMode("sell"); }}
            />
        );
    }

    const { slots, assignments, stats } = layout;
    const activeSlotDef = activeSlot ? slots.find((s) => s.key === activeSlot) : null;
    const activeSlotAssignment = activeSlot ? assignments[activeSlot] : null;
    const isCustomGround = !!layout.ground;

    return (
        <div className="space-y-8" data-testid="pitch-tab">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3 flex items-center gap-2">
                        <span>Virtual pitch</span>
                        {isCustomGround && <span className="px-1.5 py-0.5 text-[9px]" style={{ background: "var(--sb-amber)", color: "#000" }}>custom ground</span>}
                    </div>
                    <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase" data-testid="pitch-title">
                        Your real estate, in one view.
                    </h1>
                    <p className="font-editorial italic text-zinc-400 mt-3 text-lg">
                        Drag a sponsor onto a slot. Every inch of the ground is inventory.
                    </p>
                </div>
                <div className="flex items-center gap-6 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Filled</div>
                        <div className="font-display font-extrabold text-3xl tracking-tightest" data-testid="pitch-filled">
                            {stats.filled_slots}<span className="text-zinc-600 text-xl">/{stats.total_slots}</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Utilisation</div>
                        <div className="font-display font-extrabold text-3xl tracking-tightest" style={{ color: "var(--tenant-primary)" }}>
                            {stats.fill_pct}%
                        </div>
                    </div>
                    <button onClick={() => setMode("edit")}
                        className="text-xs uppercase tracking-ultra-wide border hairline px-3 py-2 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 transition-colors"
                        data-testid="ground-edit-btn">
                        <Pencil size={11} /> {isCustomGround ? "Edit ground" : "Build my ground"}
                    </button>
                </div>
            </div>

            {err && <div className="border border-rose-500/40 text-rose-300 text-xs px-3 py-2" data-testid="pitch-err">{err}</div>}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                {/* PITCH */}
                <div className="xl:col-span-9 space-y-3">
                    <SideButtonsRow slots={slots} onPickSide={(s) => setElevationSide(s)} />
                    <div className="border hairline overflow-hidden relative" data-testid="pitch-svg-wrap"
                        style={{ background: "radial-gradient(ellipse at top, rgba(245,158,11,0.08) 0%, rgba(0,0,0,0) 50%), #060b07" }}>
                        <PitchSVG
                            slots={slots}
                            assignments={assignments}
                            dragSponsorId={dragSponsorId}
                            hoverSlot={hoverSlot}
                            activeSlot={activeSlot}
                            onSlotClick={(k) => setActiveSlot(k === activeSlot ? null : k)}
                            onSlotDragEnter={(k) => setHoverSlot(k)}
                            onSlotDragLeave={() => setHoverSlot(null)}
                            onSlotDrop={(k) => {
                                if (dragSponsorId) doAssign(k, dragSponsorId);
                                setHoverSlot(null);
                                setDragSponsorId(null);
                            }}
                        />
                    </div>
                </div>

                {/* SIDEBAR */}
                <aside className="xl:col-span-3 space-y-3">
                    {/* Unassigned sponsors panel */}
                    <div className="border hairline" data-testid="unassigned-panel">
                        <div className="px-4 py-3 border-b hairline flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Bench</div>
                                <div className="font-display font-bold tracking-tight">Sponsors to place</div>
                            </div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-600">{unassigned.length}</div>
                        </div>
                        <div className="px-3 py-2 border-b hairline flex items-center gap-2">
                            <Search size={12} className="text-zinc-500" />
                            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…"
                                className="bg-transparent text-xs text-white placeholder:text-zinc-600 outline-none flex-1"
                                data-testid="pitch-sponsor-filter" />
                        </div>
                        <div className="max-h-[360px] overflow-auto">
                            {unassigned.length === 0 ? (
                                <div className="px-4 py-8 text-center text-xs text-zinc-500" data-testid="unassigned-empty">
                                    Every sponsor is on the pitch. Nicely done.
                                </div>
                            ) : unassigned.map((s) => (
                                <DraggableSponsorChip key={s.id} sponsor={s}
                                    onDragStart={() => setDragSponsorId(s.id)}
                                    onDragEnd={() => { setDragSponsorId(null); setHoverSlot(null); }}
                                    dragging={dragSponsorId === s.id}
                                />
                            ))}
                        </div>
                    </div>

                    {/* On-pitch panel */}
                    <div className="border hairline" data-testid="onpitch-panel">
                        <div className="px-4 py-3 border-b hairline flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Matchday</div>
                                <div className="font-display font-bold tracking-tight">On the pitch</div>
                            </div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-600">{assignedList.length}</div>
                        </div>
                        <div className="max-h-[280px] overflow-auto">
                            {assignedList.length === 0 ? (
                                <div className="px-4 py-8 text-center text-xs text-zinc-500">
                                    No slots filled yet. Drag a sponsor onto any dashed zone.
                                </div>
                            ) : assignedList.map((a) => {
                                const slot = slots.find((s) => s.key === a.slot_key);
                                return (
                                    <button key={a.slot_key}
                                        onClick={() => setActiveSlot(a.slot_key)}
                                        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/[0.03] border-b hairline last:border-b-0 transition-colors"
                                        data-testid={`onpitch-${a.slot_key}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs text-white truncate">{a.sponsor_name}</div>
                                            <div className="text-[10px] text-zinc-500 truncate">{slot?.label}</div>
                                        </div>
                                        <MoveRight size={12} className="text-zinc-600" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </aside>
            </div>

            {/* SLOT DETAIL PANEL */}
            {activeSlotDef && (
                <SlotDetailPanel
                    slot={activeSlotDef}
                    assignment={activeSlotAssignment}
                    sponsors={sponsors}
                    onClose={() => setActiveSlot(null)}
                    onAssign={(spId) => doAssign(activeSlotDef.key, spId)}
                    onUnassign={() => doUnassign(activeSlotDef.key)}
                />
            )}

            {/* SIDE ELEVATION VIEW (full-screen overlay) */}
            {elevationSide && (
                <SideElevationView
                    side={elevationSide}
                    slots={slots}
                    ground={layout.ground}
                    assignments={assignments}
                    sponsors={sponsors}
                    onAssign={async (slotKey, sponsorId) => { await doAssign(slotKey, sponsorId); }}
                    onUnassign={async (slotKey) => { await doUnassign(slotKey); }}
                    onClose={() => setElevationSide(null)}
                />
            )}
        </div>
    );
}

// ----- Side buttons row: lets user open the side-elevation view per side -----
const SideButtonsRow = ({ slots, onPickSide }) => {
    const sideStats = useMemo(() => {
        const stats = { N: 0, E: 0, S: 0, W: 0 };
        for (const s of slots) {
            if (s.category !== "pitchside" && s.category !== "gap") continue;
            // v2 banner/gap slots carry side in meta
            const sideKey = s.meta?.side
                || (s.key.startsWith("pb_n") ? "N"
                    : s.key.startsWith("pb_s") ? "S"
                    : s.key.startsWith("pb_e") ? "E"
                    : s.key.startsWith("pb_w") ? "W" : null);
            if (sideKey && stats[sideKey] !== undefined && s.category === "pitchside") stats[sideKey]++;
        }
        return stats;
    }, [slots]);

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="side-buttons-row">
            <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mr-1">Wall view</span>
            {["N", "E", "S", "W"].map((side) => {
                const count = sideStats[side];
                const enabled = count > 0;
                return (
                    <button key={side} onClick={() => enabled && onPickSide(side)} disabled={!enabled}
                        className={`text-[10px] uppercase tracking-ultra-wide border px-2.5 py-1.5 inline-flex items-center gap-1.5 transition-colors ${enabled ? "hairline text-zinc-300 hover:border-[var(--sb-amber)] hover:text-white" : "border-white/[0.04] text-zinc-700 cursor-not-allowed"}`}
                        data-testid={`wall-view-${side}`}>
                        <Eye size={10} /> {side === "N" ? "North" : side === "E" ? "East" : side === "S" ? "South" : "West"}
                        {enabled && <span className="text-zinc-500">· {count}</span>}
                    </button>
                );
            })}
            <span className="text-[10px] text-zinc-600 italic ml-2">Click a side to zoom in on the wall.</span>
        </div>
    );
};

// ------------- SVG PITCH -------------
const PitchSVG = ({ slots, assignments, dragSponsorId, hoverSlot, activeSlot, onSlotClick, onSlotDragEnter, onSlotDragLeave, onSlotDrop }) => {
    const isDragging = !!dragSponsorId;
    // Pitch coords inside viewBox 1000x780
    const pitch = { x: 40, y: 140, w: 920, h: 460 };
    const goal = { l_x: 32, l_y: 348, w: 8, h: 44, r_x: 960 };
    const pbox = { l: { x: 40, y: 230, w: 130, h: 280 }, r: { x: 830, y: 230, w: 130, h: 280 } };
    const sbox = { l: { x: 40, y: 290, w: 55, h: 160 }, r: { x: 905, y: 290, w: 55, h: 160 } };

    return (
        <div className="relative w-full">
            {/* Floodlight glow corners */}
            <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.12) 0%, rgba(0,0,0,0) 60%)" }} />
            <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.12) 0%, rgba(0,0,0,0) 60%)" }} />

            <svg viewBox="0 0 1000 780" className="w-full h-auto block" role="img" aria-label="Virtual pitch">
                <defs>
                    {/* Pitch stripes */}
                    <pattern id="pitchStripes" width="92" height="460" patternUnits="userSpaceOnUse">
                        <rect width="46" height="460" fill="var(--tenant-primary)" opacity="0.22" />
                        <rect x="46" width="46" height="460" fill="var(--tenant-primary)" opacity="0.32" />
                    </pattern>
                    <filter id="slotGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                </defs>

                {/* Stand (background strip at bottom) */}
                <rect x="40" y="660" width="920" height="54" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />

                {/* Pitch background */}
                <rect x={pitch.x} y={pitch.y} width={pitch.w} height={pitch.h} fill="url(#pitchStripes)" />

                {/* Pitch lines */}
                <g stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" fill="none">
                    <rect x={pitch.x} y={pitch.y} width={pitch.w} height={pitch.h} />
                    <line x1="500" y1={pitch.y} x2="500" y2={pitch.y + pitch.h} />
                    <circle cx="500" cy={pitch.y + pitch.h / 2} r="55" />
                    <circle cx="500" cy={pitch.y + pitch.h / 2} r="2" fill="rgba(255,255,255,0.55)" />
                    <rect x={pbox.l.x} y={pbox.l.y} width={pbox.l.w} height={pbox.l.h} />
                    <rect x={pbox.r.x} y={pbox.r.y} width={pbox.r.w} height={pbox.r.h} />
                    <rect x={sbox.l.x} y={sbox.l.y} width={sbox.l.w} height={sbox.l.h} />
                    <rect x={sbox.r.x} y={sbox.r.y} width={sbox.r.w} height={sbox.r.h} />
                    {/* Penalty spots */}
                    <circle cx="132" cy={pitch.y + pitch.h / 2} r="1.8" fill="rgba(255,255,255,0.55)" />
                    <circle cx="868" cy={pitch.y + pitch.h / 2} r="1.8" fill="rgba(255,255,255,0.55)" />
                    {/* Penalty arcs */}
                    <path d={`M 170 ${pitch.y + pitch.h / 2 - 30} A 32 32 0 0 1 170 ${pitch.y + pitch.h / 2 + 30}`} />
                    <path d={`M 830 ${pitch.y + pitch.h / 2 - 30} A 32 32 0 0 0 830 ${pitch.y + pitch.h / 2 + 30}`} />
                    {/* Corner arcs */}
                    <path d={`M ${pitch.x + 6} ${pitch.y} A 6 6 0 0 1 ${pitch.x} ${pitch.y + 6}`} />
                    <path d={`M ${pitch.x + pitch.w - 6} ${pitch.y} A 6 6 0 0 0 ${pitch.x + pitch.w} ${pitch.y + 6}`} />
                    <path d={`M ${pitch.x + 6} ${pitch.y + pitch.h} A 6 6 0 0 0 ${pitch.x} ${pitch.y + pitch.h - 6}`} />
                    <path d={`M ${pitch.x + pitch.w - 6} ${pitch.y + pitch.h} A 6 6 0 0 1 ${pitch.x + pitch.w} ${pitch.y + pitch.h - 6}`} />
                </g>

                {/* Goals */}
                <rect x={goal.l_x} y={goal.l_y} width={goal.w} height={goal.h} fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" />
                <rect x={goal.r_x} y={goal.l_y} width={goal.w} height={goal.h} fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" />

                {/* Kit silhouettes (behind the kit slot rectangles) */}
                <g opacity="0.3">
                    <ShirtSilhouette x={60} y={718} w={180} label="FRONT" />
                    <ShirtSilhouette x={260} y={718} w={180} label="BACK" />
                    <ShirtSilhouette x={460} y={718} w={180} label="SLEEVE" />
                </g>

                {/* SLOTS */}
                {slots.map((slot) => {
                    const assignment = assignments[slot.key];
                    const isFilled = !!assignment;
                    const isHover = hoverSlot === slot.key;
                    const isActive = activeSlot === slot.key;
                    return (
                        <SlotRect key={slot.key}
                            slot={slot}
                            assignment={assignment}
                            isFilled={isFilled}
                            isHover={isHover}
                            isActive={isActive}
                            isDragging={isDragging}
                            onClick={() => onSlotClick(slot.key)}
                            onDragOver={(e) => { e.preventDefault(); }}
                            onDragEnter={() => onSlotDragEnter(slot.key)}
                            onDragLeave={() => onSlotDragLeave()}
                            onDrop={(e) => { e.preventDefault(); onSlotDrop(slot.key); }}
                        />
                    );
                })}
            </svg>

            {/* Legend */}
            <div className="absolute right-4 top-4 flex items-center gap-4 text-[10px] uppercase tracking-ultra-wide text-zinc-500 bg-black/40 px-3 py-1.5 backdrop-blur">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 border border-dashed border-white/40" /> Open</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: "var(--tenant-primary)" }} /> Sold</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: "var(--sb-amber)" }} /> Drop here</span>
            </div>
        </div>
    );
};

// Per-slot rectangle with label + state styling
const SlotRect = ({ slot, assignment, isFilled, isHover, isActive, isDragging, onClick, onDragOver, onDragEnter, onDragLeave, onDrop }) => {
    const { x, y, w, h, label, key } = slot;
    const fill = isHover
        ? "rgba(245,158,11,0.85)"
        : isFilled
            ? "var(--tenant-primary)"
            : isDragging
                ? "rgba(245,158,11,0.15)"
                : "rgba(255,255,255,0.04)";
    const stroke = isActive
        ? "var(--sb-amber)"
        : isFilled
            ? "var(--tenant-primary)"
            : "rgba(255,255,255,0.25)";
    const strokeDash = isFilled || isHover || isActive ? "0" : "3 3";
    const opacity = isFilled ? 0.9 : isDragging ? 1 : 0.8;

    // Choose label orientation + size based on slot proportion
    const isTall = h > w;
    const fontSize = Math.min(10, Math.max(7, Math.floor((isTall ? h : w) / 16)));
    const textContent = isFilled
        ? assignment.sponsor_name
        : "Available";

    return (
        <g
            data-testid={`slot-${key}`}
            data-filled={isFilled ? "1" : "0"}
            onClick={onClick}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            style={{ cursor: "pointer" }}
        >
            <rect x={x} y={y} width={w} height={h} fill={fill} stroke={stroke}
                strokeWidth={isHover || isActive ? "1.8" : "1.2"}
                strokeDasharray={strokeDash}
                opacity={opacity}
                filter={isHover ? "url(#slotGlow)" : undefined}
            >
                {!isFilled && !isDragging && (
                    <animate attributeName="opacity" values="0.35;0.8;0.35" dur="2.8s" repeatCount="indefinite" />
                )}
            </rect>
            {/* Text label inside slot (if space allows) */}
            {(w >= 36 && h >= 14) && (
                <text
                    x={x + w / 2}
                    y={y + h / 2 + fontSize / 3}
                    textAnchor="middle"
                    fill={isFilled ? "var(--tenant-primary-foreground, #000)" : "rgba(255,255,255,0.8)"}
                    fontSize={fontSize}
                    fontWeight={isFilled ? 700 : 500}
                    letterSpacing="0.4"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif", pointerEvents: "none", textTransform: "uppercase" }}
                >
                    {truncate(textContent, Math.floor(w / (fontSize * 0.55)))}
                </text>
            )}
            {/* External tooltip label for tiny slots (goal nets) */}
            {(w < 36 || h < 14) && (
                <text x={x + w / 2} y={y - 4} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7" letterSpacing="0.5"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif", pointerEvents: "none", textTransform: "uppercase" }}>
                    {label.split("·")[0].trim()}
                </text>
            )}
        </g>
    );
};

const ShirtSilhouette = ({ x, y, w, label }) => {
    // Very simple shirt: rectangle with tapered top
    const h = 42;
    return (
        <g>
            <path
                d={`M ${x + 12} ${y} L ${x + w * 0.3} ${y} L ${x + w * 0.38} ${y + 6} L ${x + w * 0.62} ${y + 6} L ${x + w * 0.7} ${y} L ${x + w - 12} ${y} L ${x + w} ${y + 12} L ${x + w - 8} ${y + h} L ${x + 8} ${y + h} L ${x} ${y + 12} Z`}
                fill="rgba(255,255,255,0.04)"
                stroke="rgba(255,255,255,0.15)"
            />
            <text x={x + w / 2} y={y + h + 10} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" letterSpacing="1.5"
                style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                SHIRT · {label}
            </text>
        </g>
    );
};

const truncate = (s, max) => {
    if (!s) return "";
    if (s.length <= max) return s;
    if (max < 3) return s.slice(0, max);
    return s.slice(0, Math.max(1, max - 1)) + "…";
};

// ------------- DRAGGABLE SPONSOR CHIP -------------
const DraggableSponsorChip = ({ sponsor, onDragStart, onDragEnd, dragging }) => {
    const dragImgRef = useRef(null);
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/sponsor-id", sponsor.id);
                onDragStart?.();
            }}
            onDragEnd={onDragEnd}
            ref={dragImgRef}
            className={`flex items-center gap-2.5 px-3 py-2.5 border-b hairline last:border-b-0 cursor-grab active:cursor-grabbing hover:bg-white/[0.04] transition-colors ${dragging ? "opacity-40" : ""}`}
            data-testid={`drag-sponsor-${sponsor.id}`}
        >
            <div className="w-7 h-7 bg-white/[0.06] border hairline flex items-center justify-center text-[9px] font-bold text-zinc-300 shrink-0">
                {sponsor.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{sponsor.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{sponsor.tier} · {formatGBP(sponsor.monthly_gbp)}/mo</div>
            </div>
        </div>
    );
};

// ------------- SLOT DETAIL PANEL -------------
const SlotDetailPanel = ({ slot, assignment, sponsors, onClose, onAssign, onUnassign }) => {
    const [showPicker, setShowPicker] = useState(false);
    const available = sponsors.filter((s) => s.status !== "Cancelled");
    return (
        <div className="fixed inset-0 z-40 flex items-end md:items-center md:justify-end pointer-events-none" data-testid="slot-detail">
            <div className="pointer-events-auto bg-[#060b07] border-l border-t border-b hairline w-full md:w-[420px] md:h-auto md:max-h-[80vh] md:mr-0 md:my-auto md:mt-20 p-6 overflow-auto"
                style={{ boxShadow: "-20px 0 60px rgba(0,0,0,0.7)" }}>
                <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">{slot.category}</div>
                        <h3 className="font-display font-extrabold text-2xl tracking-tight leading-tight">{slot.label}</h3>
                    </div>
                    <button onClick={onClose} className="p-1 text-zinc-400 hover:text-white" data-testid="slot-detail-close"><X size={18} /></button>
                </div>

                {assignment ? (
                    <>
                        <div className="border p-5 mb-4" style={{ borderColor: "var(--tenant-primary)" }}>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-ultra-wide mb-2" style={{ color: "var(--tenant-primary)" }}>
                                <CheckCircle2 size={12} /> Allocated
                            </div>
                            <div className="font-display font-extrabold text-xl tracking-tight" data-testid="slot-assigned-name">{assignment.sponsor_name}</div>
                            <div className="text-xs text-zinc-400 mt-1">{assignment.sponsor_tier} · {formatGBP(assignment.monthly_gbp)}/mo · {assignment.sponsor_status}</div>
                        </div>
                        <div className="space-y-2">
                            <button onClick={() => setShowPicker(true)} className="w-full text-sm border hairline px-4 py-2.5 hover:border-white/30 transition-colors text-left flex items-center justify-between"
                                data-testid="slot-reassign">
                                Reassign to another sponsor <MoveRight size={14} />
                            </button>
                            <button onClick={() => { if (window.confirm("Remove this sponsor from the slot?")) onUnassign(); }}
                                className="w-full text-sm border border-rose-500/40 text-rose-300 px-4 py-2.5 hover:bg-rose-500/5 transition-colors text-left"
                                data-testid="slot-unassign">
                                Clear slot
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="border border-dashed hairline p-5 mb-4 text-center" data-testid="slot-empty-state">
                            <Circle size={16} className="text-zinc-500 mx-auto mb-2" />
                            <div className="font-display font-bold text-sm tracking-tight">Available</div>
                            <div className="text-xs text-zinc-500 mt-1">Drag a sponsor onto this slot — or pick one below.</div>
                        </div>
                        <button onClick={() => setShowPicker(true)} className="w-full text-sm px-4 py-2.5 font-display font-bold tracking-tight transition-colors"
                            style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}
                            data-testid="slot-assign-btn">
                            Assign sponsor
                        </button>
                    </>
                )}

                {showPicker && (
                    <div className="mt-5 border hairline">
                        <div className="px-4 py-2 border-b hairline text-[10px] uppercase tracking-ultra-wide text-zinc-500">Pick sponsor</div>
                        <div className="max-h-64 overflow-auto">
                            {available.length === 0 ? (
                                <div className="px-4 py-6 text-center text-xs text-zinc-500">No sponsors yet — add one from the Sponsors tab.</div>
                            ) : available.map((s) => (
                                <button key={s.id} onClick={() => { onAssign(s.id); setShowPicker(false); }}
                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/[0.04] border-b hairline last:border-b-0 flex items-center justify-between transition-colors"
                                    data-testid={`picker-${s.id}`}>
                                    <span className="truncate">{s.name}</span>
                                    <span className="text-[10px] text-zinc-500 shrink-0 ml-2">{s.tier} · {formatGBP(s.monthly_gbp)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {!assignment && !showPicker && (
                    <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed flex items-start gap-2">
                        <Sparkles size={11} className="mt-0.5 text-[var(--sb-amber)] shrink-0" />
                        Tip: slot categories — kit, pitchside, stand, dugout, goal, broadcast — can be used to build themed sponsor packages later.
                    </p>
                )}
            </div>
        </div>
    );
};
